import React, { useEffect, useMemo, useState } from 'react';
import { Button, DsoLoadingSpinner, Radio } from 'components/core';
import { MdLocationOn } from 'react-icons/md';
import { AddressSearch } from '../AddressSearch';
import {
  useRetrieveDsoBillingAddresses,
  useRetrieveDsoBillingAddressesByShipAccount,
  useRetrieveDsoShippingAddresses,
} from 'reactQuery/queries';
import {
  DsoOrderAddressProps,
  EditModeProps,
  MultiShipBillingProps,
  SingleShipProps,
} from './DsoOrderAddress.types';
import * as Styled from './DsoOrderAddress.styles';
import {
  displayAddress,
  areAllBillingAddressesSelected,
  getCommonBillingAddresses,
} from './DsoOrderAddress.helper';

// ─────────────────────────────────────────────────────────────────────────────
// EDIT MODE (read-only summary)
// ─────────────────────────────────────────────────────────────────────────────

const EditMode: React.FC<EditModeProps> = ({
  isMultiShipOrder,
  billingGroups,
  shippingAddresses,
  selectedShipToAccountNumber,
  selectedBillToAccountNumbers,
}) => {
  const getShipAddress = (shipAccountNumber: string) =>
    shippingAddresses?.find(a => a.accountNumber === shipAccountNumber);

  // For single-ship, build a single-entry list matching the same shape
  const groups = isMultiShipOrder
    ? billingGroups
    : billingGroups?.length
    ? [{ ...billingGroups[0], shipAccountNumber: selectedShipToAccountNumber! }]
    : [];

  if (!groups?.length) return null;

  return (
    <Styled.Container>
      {groups.map(group => {
        const selectedBill =
          selectedBillToAccountNumbers[group.shipAccountNumber];
        const billingAddress = group.billingAddresses.find(
          a => a.accountNumber === selectedBill
        );
        if (!billingAddress) return null;
        const shipAddress = getShipAddress(group.shipAccountNumber);
        return (
          <Styled.Card key={group.shipAccountNumber}>
            <Styled.CardHeader>
              <Styled.CardIcon>
                <MdLocationOn size={16} />
              </Styled.CardIcon>
              <Styled.CardName>
                {shipAddress?.accountName || group.shipAccountNumber}
              </Styled.CardName>
            </Styled.CardHeader>
            <Styled.EditAddressRow>
              <Styled.EditAddressBlock>
                <Styled.EditAddressLabel>Ship To</Styled.EditAddressLabel>
                <Styled.EditAddressValue>
                  {displayAddress(shipAddress)}
                </Styled.EditAddressValue>
              </Styled.EditAddressBlock>
              <Styled.EditAddressBlock>
                <Styled.EditAddressLabel>Bill To</Styled.EditAddressLabel>
                <Styled.EditAddressValue>
                  {displayAddress(billingAddress)}
                </Styled.EditAddressValue>
              </Styled.EditAddressBlock>
            </Styled.EditAddressRow>
          </Styled.Card>
        );
      })}
    </Styled.Container>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CREATE MODE — Multi-Ship Billing
// ─────────────────────────────────────────────────────────────────────────────

const MultiShipBilling: React.FC<MultiShipBillingProps> = ({
  billingGroups,
  shippingAddresses,
  isBillingLoading,
  selectedBillToAccountNumbers,
  commonBillingAddresses,
  hasCommonBilling,
  useSameBilling,
  setUseSameBilling,
  setSelectedBillToAccountNumbers,
  onBillingChange,
  onSameBillingSelect,
  selectedSameBillingValue,
}) => {
  const getShipLabel = (shipAccountNumber: string) =>
    shippingAddresses?.find(a => a.accountNumber === shipAccountNumber)
      ?.accountName || shipAccountNumber;

  const getShipFullAddress = (shipAccountNumber: string) =>
    shippingAddresses?.find(a => a.accountNumber === shipAccountNumber)
      ?.accountAddress || '';

  const shippingCount = billingGroups?.length ?? 0;

  // Case 1: Common billing addresses exist — ask same or different
  if (hasCommonBilling) {
    return (
      <Styled.Section>
        <Styled.Card>
          <Styled.BillingQuestion>
            Would you like to use the same billing address for all{' '}
            {shippingCount} shipping locations?
          </Styled.BillingQuestion>

          <Styled.RadioGroup>
            <Styled.RadioCard
              $selected={useSameBilling === true}
              onClick={() => {
                if (useSameBilling !== true) {
                  setUseSameBilling(true);
                  setSelectedBillToAccountNumbers({});
                }
              }}
            >
              <Radio
                label=""
                checked={useSameBilling === true}
                onChange={() => {
                  if (useSameBilling !== true) {
                    setUseSameBilling(true);
                    setSelectedBillToAccountNumbers({});
                  }
                }}
              />
              <Styled.RadioCardContent>
                <Styled.RadioCardLabel>Yes, same for all</Styled.RadioCardLabel>
                <Styled.RadioCardDescription>
                  One Bill-to invoices every location
                </Styled.RadioCardDescription>
              </Styled.RadioCardContent>
            </Styled.RadioCard>

            <Styled.RadioCard
              $selected={useSameBilling === false}
              onClick={() => {
                if (useSameBilling !== false) {
                  setUseSameBilling(false);
                  setSelectedBillToAccountNumbers({});
                }
              }}
            >
              <Radio
                label=""
                checked={useSameBilling === false}
                onChange={() => {
                  if (useSameBilling !== false) {
                    setUseSameBilling(false);
                    setSelectedBillToAccountNumbers({});
                  }
                }}
              />
              <Styled.RadioCardContent>
                <Styled.RadioCardLabel>
                  No, different for each
                </Styled.RadioCardLabel>
                <Styled.RadioCardDescription>
                  Choose a Bill-to per shipping location
                </Styled.RadioCardDescription>
              </Styled.RadioCardContent>
            </Styled.RadioCard>
          </Styled.RadioGroup>

          {/* Same for all → single dropdown */}
          {useSameBilling === true && (
            <Styled.OverrideSection>
              <AddressSearch
                addresses={commonBillingAddresses}
                isLoading={isBillingLoading}
                selectedValue={selectedSameBillingValue}
                onChange={onSameBillingSelect}
                placeholder="Select Billing Address"
                width="100%"
                size="md"
                noBorder={false}
                showAccountNumber
              />
            </Styled.OverrideSection>
          )}

          {/* Different for each → per-ship cards */}
          {useSameBilling === false && (
            <Styled.OverrideSection>
              {billingGroups?.map(group => (
                <Styled.Card key={group.shipAccountNumber}>
                  <Styled.CardRow>
                    <Styled.CardShipSection>
                      <Styled.CardBillLabel>Ship To</Styled.CardBillLabel>
                      <Styled.CardHeader>
                        <Styled.CardIcon>
                          <MdLocationOn size={16} />
                        </Styled.CardIcon>
                        <Styled.CardName>
                          {getShipLabel(group.shipAccountNumber)}{' '}
                          <span
                            style={{
                              fontWeight: 400,
                              color: 'rgba(0, 0, 0, 0.55)',
                            }}
                          >
                            ({group.shipAccountNumber})
                          </span>
                        </Styled.CardName>
                      </Styled.CardHeader>
                      <Styled.CardAddress>
                        {getShipFullAddress(group.shipAccountNumber)}
                      </Styled.CardAddress>
                    </Styled.CardShipSection>
                    <Styled.CardBillSection>
                      <Styled.CardBillLabel>Bill To</Styled.CardBillLabel>
                      <AddressSearch
                        addresses={group.billingAddresses}
                        isLoading={isBillingLoading}
                        selectedValue={
                          selectedBillToAccountNumbers[group.shipAccountNumber]
                        }
                        onChange={value =>
                          onBillingChange(group.shipAccountNumber, value)
                        }
                        placeholder="Select Billing Address"
                        width="100%"
                        size="md"
                        noBorder={false}
                        showAccountNumber
                      />
                    </Styled.CardBillSection>
                  </Styled.CardRow>
                </Styled.Card>
              ))}
            </Styled.OverrideSection>
          )}
        </Styled.Card>
      </Styled.Section>
    );
  }

  // Case 2: No common billing — show per-ship dropdowns directly
  return (
    <>
      {billingGroups?.map(group => (
        <Styled.Section key={group.shipAccountNumber}>
          <Styled.Title>
            Billing Address for {getShipLabel(group.shipAccountNumber)}
          </Styled.Title>
          {group.billingAddresses.length === 1 ? (
            <div>
              {displayAddress(group.billingAddresses[0])}
              <Styled.AutoSelectedNote>(auto-selected)</Styled.AutoSelectedNote>
            </div>
          ) : (
            <AddressSearch
              addresses={group.billingAddresses}
              isLoading={isBillingLoading}
              selectedValue={
                selectedBillToAccountNumbers[group.shipAccountNumber]
              }
              onChange={value =>
                onBillingChange(group.shipAccountNumber, value)
              }
              placeholder="Select Billing Address"
              width="100%"
              size="md"
              noBorder={false}
              showAccountNumber
            />
          )}
        </Styled.Section>
      ))}
    </>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// CREATE MODE — Single-Ship
// ─────────────────────────────────────────────────────────────────────────────

const SingleShipCreate: React.FC<SingleShipProps> = ({
  shippingAddresses,
  isShippingLoading,
  isBillingLoading,
  selectedShipToAccountNumber,
  setSelectedShipToAccountNumber,
  selectedBillToAccountNumbers,
  currentBillingGroup,
  onBillingChange,
}) => (
  <>
    <Styled.Section>
      <Styled.Title>Shipping Address</Styled.Title>
      <AddressSearch
        addresses={shippingAddresses ?? []}
        isLoading={isShippingLoading}
        selectedValue={selectedShipToAccountNumber}
        onChange={setSelectedShipToAccountNumber}
        placeholder="Select Shipping Address"
        width="100%"
        size="md"
        noBorder={false}
        showAccountNumber
      />
    </Styled.Section>

    <Styled.Section>
      <Styled.Title>Billing Address</Styled.Title>
      <AddressSearch
        key={selectedShipToAccountNumber ?? 'no-ship'}
        addresses={
          selectedShipToAccountNumber && currentBillingGroup
            ? currentBillingGroup.billingAddresses
            : []
        }
        isLoading={isBillingLoading}
        selectedValue={
          selectedShipToAccountNumber
            ? selectedBillToAccountNumbers[selectedShipToAccountNumber]
            : undefined
        }
        onChange={value =>
          selectedShipToAccountNumber &&
          onBillingChange(selectedShipToAccountNumber, value)
        }
        placeholder="Select Billing Address"
        width="100%"
        size="md"
        noBorder={false}
        showAccountNumber
        disabled={!selectedShipToAccountNumber}
      />
    </Styled.Section>
  </>
);

// ─────────────────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────────────────

const DsoOrderAddress: React.FC<DsoOrderAddressProps> = ({
  isMultiShipOrder,
  selectedShipToAccountNumber,
  setSelectedShipToAccountNumber,
  selectedBillToAccountNumbers,
  setSelectedBillToAccountNumbers,
  mode,
  onSave,
  isSaveAddressMutationLoading,
}) => {
  // ── Data fetching ──────────────────────────────────────────────────────────
  // Multi-ship: fetch all billing groups upfront
  const { data: allBillingGroups, isLoading: isAllBillingLoading } =
    useRetrieveDsoBillingAddresses();

  // Single-ship: fetch billing for selected shipping address only
  const { data: singleShipBillingGroups, isLoading: isSingleBillingLoading } =
    useRetrieveDsoBillingAddressesByShipAccount(
      !isMultiShipOrder ? selectedShipToAccountNumber : undefined
    );

  const billingGroups = isMultiShipOrder
    ? allBillingGroups
    : singleShipBillingGroups;
  const isBillingLoading = isMultiShipOrder
    ? isAllBillingLoading
    : isSingleBillingLoading;

  const { data: shippingAddresses, isLoading: isShippingLoading } =
    useRetrieveDsoShippingAddresses();

  // ── Multi-ship state ───────────────────────────────────────────────────────
  const [useSameBilling, setUseSameBilling] = useState<boolean | null>(true);

  const commonBillingAddresses = useMemo(
    () => getCommonBillingAddresses(billingGroups),
    [billingGroups]
  );

  const hasCommonBilling =
    isMultiShipOrder && commonBillingAddresses.length > 0;

  // ── Auto-select billing when only one option exists ────────────────────────
  useEffect(() => {
    if (!billingGroups) return;

    // Multi-ship "same for all" with single common billing → select for all groups
    if (
      isMultiShipOrder &&
      hasCommonBilling &&
      useSameBilling === true &&
      commonBillingAddresses.length === 1
    ) {
      const singleBilling = commonBillingAddresses[0].accountNumber;
      const alreadySelected =
        selectedBillToAccountNumbers[billingGroups[0]?.shipAccountNumber] ===
        singleBilling;
      if (!alreadySelected) {
        const newSelections: Record<string, string> = {};
        for (const group of billingGroups) {
          newSelections[group.shipAccountNumber] = singleBilling;
        }
        setSelectedBillToAccountNumbers(newSelections);
      }
      return;
    }

    // Per-group: auto-select when group has exactly one billing address
    const newSelections = { ...selectedBillToAccountNumbers };
    let changed = false;
    for (const group of billingGroups) {
      if (
        group.billingAddresses.length === 1 &&
        !newSelections[group.shipAccountNumber]
      ) {
        newSelections[group.shipAccountNumber] =
          group.billingAddresses[0].accountNumber;
        changed = true;
      }
    }
    if (changed) {
      setSelectedBillToAccountNumbers(newSelections);
    }
  }, [
    billingGroups,
    commonBillingAddresses,
    useSameBilling,
    hasCommonBilling,
    isMultiShipOrder,
  ]);

  // ── Handlers ───────────────────────────────────────────────────────────────
  const handleSameBillingSelect = (value: string | undefined) => {
    if (!billingGroups) return;
    if (!value) {
      setSelectedBillToAccountNumbers({});
      return;
    }
    const newSelections: Record<string, string> = {};
    for (const group of billingGroups) {
      newSelections[group.shipAccountNumber] = value;
    }
    setSelectedBillToAccountNumbers(newSelections);
  };

  const handleBillingChange = (
    shipAccountNumber: string,
    value: string | undefined
  ) => {
    setSelectedBillToAccountNumbers({
      ...selectedBillToAccountNumbers,
      [shipAccountNumber]: value ?? '',
    });
  };

  // ── Loading state ──────────────────────────────────────────────────────────
  const isInitialLoading = isMultiShipOrder
    ? isAllBillingLoading || isShippingLoading
    : isShippingLoading;

  if (isInitialLoading) {
    return <DsoLoadingSpinner />;
  }

  // ── Edit mode (read-only summary) ─────────────────────────────────────────
  if (mode === 'edit') {
    return (
      <EditMode
        isMultiShipOrder={isMultiShipOrder}
        billingGroups={billingGroups}
        shippingAddresses={shippingAddresses}
        selectedShipToAccountNumber={selectedShipToAccountNumber}
        selectedBillToAccountNumbers={selectedBillToAccountNumbers}
      />
    );
  }

  // ── Create mode ────────────────────────────────────────────────────────────
  const currentBillingGroup = isMultiShipOrder
    ? billingGroups?.find(
        g => g.shipAccountNumber === selectedShipToAccountNumber
      )
    : billingGroups?.[0];

  const allBillingSelected = areAllBillingAddressesSelected(
    billingGroups,
    selectedBillToAccountNumbers,
    isMultiShipOrder,
    selectedShipToAccountNumber
  );

  const selectedSameBillingValue =
    useSameBilling && billingGroups?.length
      ? selectedBillToAccountNumbers[billingGroups[0].shipAccountNumber]
      : undefined;

  return (
    <Styled.Container>
      {isMultiShipOrder ? (
        <MultiShipBilling
          billingGroups={billingGroups}
          shippingAddresses={shippingAddresses}
          isBillingLoading={isBillingLoading}
          selectedBillToAccountNumbers={selectedBillToAccountNumbers}
          commonBillingAddresses={commonBillingAddresses}
          hasCommonBilling={hasCommonBilling}
          useSameBilling={useSameBilling}
          setUseSameBilling={setUseSameBilling}
          setSelectedBillToAccountNumbers={setSelectedBillToAccountNumbers}
          onBillingChange={handleBillingChange}
          onSameBillingSelect={handleSameBillingSelect}
          selectedSameBillingValue={selectedSameBillingValue}
        />
      ) : (
        <SingleShipCreate
          shippingAddresses={shippingAddresses}
          isShippingLoading={isShippingLoading}
          isBillingLoading={isBillingLoading}
          selectedShipToAccountNumber={selectedShipToAccountNumber}
          setSelectedShipToAccountNumber={setSelectedShipToAccountNumber}
          selectedBillToAccountNumbers={selectedBillToAccountNumbers}
          currentBillingGroup={currentBillingGroup}
          onBillingChange={handleBillingChange}
        />
      )}

      <Styled.ButtonWrapper>
        <Button
          size="md"
          onClick={onSave}
          disabled={
            isSaveAddressMutationLoading ||
            isBillingLoading ||
            isShippingLoading ||
            (!isMultiShipOrder && !selectedShipToAccountNumber) ||
            (isMultiShipOrder && hasCommonBilling && useSameBilling === null) ||
            !allBillingSelected
          }
          loading={isSaveAddressMutationLoading}
        >
          Continue to Shipping Method
        </Button>
      </Styled.ButtonWrapper>
    </Styled.Container>
  );
};

export default DsoOrderAddress;
